import type { MarketingFormField } from "@/lib/api/forms";

const CONTACT_INFO_STORAGE_KEY = "em_contact_info_v1";
const CONTACT_INFO_STORAGE_VERSION = 1;
const CONTACT_INFO_CHANGE_EVENT = "em_contact_info_changed";

type ContactInfoField =
  | "name"
  | "full_name"
  | "first_name"
  | "last_name"
  | "email"
  | "phone"
  | "company"
  | "address_line1"
  | "address_line2"
  | "city"
  | "state"
  | "zip"
  | "country";

export type ContactInfo = Partial<Record<ContactInfoField, string>>;

interface StoredContactInfo {
  version: number;
  values: ContactInfo;
}

const CONTACT_FIELD_ALIASES: Record<string, ContactInfoField> = {
  name: "name",
  full_name: "full_name",
  fullname: "full_name",
  first_name: "first_name",
  firstname: "first_name",
  last_name: "last_name",
  lastname: "last_name",
  email: "email",
  phone: "phone",
  company: "company",
  address_line1: "address_line1",
  addressline1: "address_line1",
  address_line2: "address_line2",
  addressline2: "address_line2",
  city: "city",
  state: "state",
  zip: "zip",
  postal_code: "zip",
  postalcode: "zip",
  country: "country",
};

const EXCLUDED_KEYS = new Set([
  "message",
  "notes",
  "issue_description",
  "budget",
  "timeline",
  "consent",
  "marketing_use_consent",
  "marketing_use_disclosure_text",
  "source_url",
  "visitor_id",
  "recaptcha_token",
  "g_recaptcha_response",
  "website",
]);

const CONTACT_AUTOCOMPLETE: Record<ContactInfoField, string> = {
  name: "name",
  full_name: "name",
  first_name: "given-name",
  last_name: "family-name",
  email: "email",
  phone: "tel",
  company: "organization",
  address_line1: "address-line1",
  address_line2: "address-line2",
  city: "address-level2",
  state: "address-level1",
  zip: "postal-code",
  country: "country-name",
};

const AUTOCOMPLETE_FIELD_TYPES = new Set<MarketingFormField["field_type"]>([
  "text",
  "email",
  "phone",
  "zip",
]);

const BLOCKED_FIELD_TYPES = new Set<MarketingFormField["field_type"]>([
  "checkbox",
  "radio",
  "date",
  "consent",
]);

export function loadContactInfo(): ContactInfo {
  const storage = getLocalStorage();
  if (!storage) return {};

  try {
    const raw = storage.getItem(CONTACT_INFO_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed) || parsed.version !== CONTACT_INFO_STORAGE_VERSION || !isRecord(parsed.values)) {
      return {};
    }

    return normalizeContactInfo(parsed.values);
  } catch {
    return {};
  }
}

export function saveContactInfoFromSubmittedValues(
  values: Record<string, unknown>,
  fields?: Pick<MarketingFormField, "field_key" | "field_type">[],
): ContactInfo {
  const extracted = extractContactInfo(values, fields);
  const submitted = normalizeContactInfo(extracted);
  if (!hasContactInfo(submitted)) {
    return loadContactInfo();
  }

  const presentFields = getSubmittedContactFields(extracted, true);
  const nonEmptyFields = getSubmittedContactFields(extracted, false);
  const next = normalizeSubmittedNameAliases(
    normalizeContactInfo({
      ...loadContactInfo(),
      ...submitted,
    }),
    submitted,
    presentFields,
    nonEmptyFields,
  );

  if (persistContactInfo(next)) {
    emitContactInfoChange(next);
  }

  return next;
}

export function prefillMarketingFormValues<T extends Record<string, unknown>>(
  fields: Pick<MarketingFormField, "field_key" | "field_type">[],
  values: T,
): T {
  const contactInfo = loadContactInfo();
  if (!hasContactInfo(contactInfo)) return values;

  let next: Record<string, unknown> | null = null;

  for (const field of fields) {
    if (!isContactFormField(field) || !isEmptyFormValue(values[field.field_key])) {
      continue;
    }

    const value = getContactValueForKey(contactInfo, field.field_key);
    if (!value) continue;

    next = next ?? { ...values };
    next[field.field_key] = value;
  }

  return (next ?? values) as T;
}

export function prefillContactInfoValues<T extends Record<string, unknown>>(values: T): T {
  const contactInfo = loadContactInfo();
  if (!hasContactInfo(contactInfo)) return values;

  let next: Record<string, unknown> | null = null;

  for (const key of Object.keys(values)) {
    if (!isEmptyFormValue(values[key])) continue;

    const value = getContactValueForKey(contactInfo, key);
    if (!value) continue;

    next = next ?? { ...values };
    next[key] = value;
  }

  return (next ?? values) as T;
}

export function getContactFieldAutoComplete(
  field: Pick<MarketingFormField, "field_key" | "field_type">,
): string | undefined {
  if (!AUTOCOMPLETE_FIELD_TYPES.has(field.field_type)) return undefined;

  const contactField = contactFieldForKey(field.field_key);
  return contactField ? CONTACT_AUTOCOMPLETE[contactField] : undefined;
}

export function emitContactInfoChange(contactInfo: ContactInfo = loadContactInfo()) {
  if (typeof window === "undefined") return;

  try {
    window.dispatchEvent(new CustomEvent<ContactInfo>(CONTACT_INFO_CHANGE_EVENT, { detail: contactInfo }));
  } catch {
    // Ignore browsers or modes that block custom events.
  }
}

export function subscribeToContactInfoChange(listener: (contactInfo: ContactInfo) => void) {
  if (typeof window === "undefined") return () => undefined;

  const handleChange = (event: Event) => {
    listener(event instanceof CustomEvent ? event.detail : loadContactInfo());
  };

  window.addEventListener(CONTACT_INFO_CHANGE_EVENT, handleChange);
  return () => window.removeEventListener(CONTACT_INFO_CHANGE_EVENT, handleChange);
}

function extractContactInfo(
  values: Record<string, unknown>,
  fields?: Pick<MarketingFormField, "field_key" | "field_type">[],
): Record<string, unknown> {
  if (!fields) return values;

  return Object.fromEntries(
    fields
      .filter(isContactFormField)
      .map((field) => [field.field_key, values[field.field_key]]),
  );
}

function getSubmittedContactFields(values: Record<string, unknown>, includeEmpty: boolean) {
  const submittedFields = new Set<ContactInfoField>();

  for (const [key, value] of Object.entries(values)) {
    const contactField = contactFieldForKey(key);
    if (!contactField) continue;

    if (includeEmpty || normalizeContactValue(value)) {
      submittedFields.add(contactField);
    }
  }

  return submittedFields;
}

function normalizeSubmittedNameAliases(
  contactInfo: ContactInfo,
  submitted: ContactInfo,
  presentFields: Set<ContactInfoField>,
  nonEmptyFields: Set<ContactInfoField>,
) {
  const next = { ...contactInfo };
  const submittedFullName = submitted.full_name || submitted.name;
  if ((nonEmptyFields.has("full_name") || nonEmptyFields.has("name")) && submittedFullName) {
    next.full_name = submittedFullName;
    next.name = submittedFullName;

    const nameParts = splitFullName(submittedFullName);
    next.first_name = nameParts.first_name;
    if (nameParts.last_name) {
      next.last_name = nameParts.last_name;
    } else {
      delete next.last_name;
    }

    return next;
  }

  if (!nonEmptyFields.has("first_name") && !nonEmptyFields.has("last_name")) {
    return next;
  }

  if (presentFields.has("first_name")) {
    if (submitted.first_name) {
      next.first_name = submitted.first_name;
    } else {
      delete next.first_name;
    }
  }

  if (presentFields.has("last_name")) {
    if (submitted.last_name) {
      next.last_name = submitted.last_name;
    } else {
      delete next.last_name;
    }
  }

  const combinedName = [next.first_name, next.last_name].filter(Boolean).join(" ");
  if (combinedName) {
    next.full_name = combinedName;
    next.name = combinedName;
  } else {
    delete next.full_name;
    delete next.name;
  }

  return next;
}

function isContactFormField(field: Pick<MarketingFormField, "field_key" | "field_type">) {
  return !BLOCKED_FIELD_TYPES.has(field.field_type) && contactFieldForKey(field.field_key) !== null;
}

function contactFieldForKey(rawKey: string): ContactInfoField | null {
  const key = normalizeKey(rawKey);
  if (
    EXCLUDED_KEYS.has(key) ||
    key.startsWith("boat_") ||
    key.startsWith("utm_") ||
    key.includes("consent") ||
    key.includes("recaptcha") ||
    key.includes("captcha") ||
    key.includes("upload")
  ) {
    return null;
  }

  return CONTACT_FIELD_ALIASES[key] ?? null;
}

function getContactValueForKey(contactInfo: ContactInfo, key: string) {
  const contactField = contactFieldForKey(key);
  return contactField ? contactInfo[contactField] : undefined;
}

function normalizeContactInfo(values: Record<string, unknown>): ContactInfo {
  const contactInfo: ContactInfo = {};

  for (const [key, value] of Object.entries(values)) {
    const contactField = contactFieldForKey(key);
    const contactValue = normalizeContactValue(value);
    if (contactField && contactValue) {
      contactInfo[contactField] = contactValue;
    }
  }

  const fullName = contactInfo.full_name || contactInfo.name;
  if (fullName) {
    contactInfo.full_name = fullName;
    contactInfo.name = fullName;

    const nameParts = splitFullName(fullName);
    if (!contactInfo.first_name && nameParts.first_name) {
      contactInfo.first_name = nameParts.first_name;
    }
    if (!contactInfo.last_name && nameParts.last_name) {
      contactInfo.last_name = nameParts.last_name;
    }
  }

  const combinedName = [contactInfo.first_name, contactInfo.last_name].filter(Boolean).join(" ");
  if (combinedName) {
    contactInfo.full_name = contactInfo.full_name || combinedName;
    contactInfo.name = contactInfo.name || combinedName;
  }

  return contactInfo;
}

function splitFullName(fullName: string) {
  const parts = fullName.trim().split(/\s+/);
  return {
    first_name: parts[0],
    last_name: parts.length > 1 ? parts.slice(1).join(" ") : undefined,
  };
}

function normalizeContactValue(value: unknown) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

function normalizeKey(key: string) {
  return key
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[-\s]+/g, "_");
}

function isEmptyFormValue(value: unknown) {
  return value === undefined || value === null || (typeof value === "string" && value.trim() === "");
}

function hasContactInfo(contactInfo: ContactInfo) {
  return Object.values(contactInfo).some(Boolean);
}

function persistContactInfo(contactInfo: ContactInfo) {
  const storage = getLocalStorage();
  if (!storage) return false;

  try {
    const stored: StoredContactInfo = {
      version: CONTACT_INFO_STORAGE_VERSION,
      values: contactInfo,
    };
    storage.setItem(CONTACT_INFO_STORAGE_KEY, JSON.stringify(stored));
    return true;
  } catch {
    return false;
  }
}

function getLocalStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
